import * as Styled from './OxDoctorProfile.styled';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { OxTextTagline } from 'src/components/OxTextTagline';
import { OxTextHeading } from 'src/components/OxTextHeading';
import { OxTextParagraph } from 'src/components/OxTextParagraph';
import { OxVideoPlayer } from 'src/components/OxVideoPlayer';
import { OxIcon } from 'src/components/OxIcon';
import { Videos } from 'src/services/cms/cms.types';
import { TFluidImages } from 'src/components/OxImage';
import { DOCTOR_PROFILE_SWITCH_EVENT } from 'src/constants/events';

type TProps = {
    fluidImages?: TFluidImages;
    video?: Videos;
    label: string;
    title?: string;
    description: string;
    isFirst?: boolean;
    contentHeight?: (number | null)[];
    assetHeight?: (number | null)[];
    doctorId?: string; // Add unique identifier for each doctor
};

export const OxDoctorProfile = ({
    fluidImages,
    video,
    label,
    title,
    description,
    isFirst,
    contentHeight,
    assetHeight,
    doctorId
}: TProps): JSX.Element => {
    const [showVideo, setShowVideo] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Check if video exists and has valid assets
    const hasVideo = video && video.length > 0 && video.some((v) => v.mux?.asset?.playbackId);
    const hasImages = fluidImages;

    // Properly filter and prepare videos for the video player
    // The OxVideoPlayer will handle device filtering internally based on the device property
    const videoForPlayer = useMemo(() => {
        if (!video) return [];

        return video.filter((v) => v.mux?.asset?.playbackId); // Only include videos with valid assets
    }, [video]);

    // Pause all other videos on the page when this component's video plays
    useEffect(() => {
        const handleVideoPlay = () => {
            // Pause all other video elements on the page
            const allVideos = document.querySelectorAll('video');
            allVideos.forEach((video) => {
                if (video !== videoRef.current && !video.paused) {
                    video.pause();
                }
            });
        };

        const video = videoRef.current;
        if (video && showVideo) {
            video.addEventListener('play', handleVideoPlay);
            return () => {
                video.removeEventListener('play', handleVideoPlay);
            };
        }
    }, [showVideo]);

    // Pause video when component unmounts
    useEffect(() => {
        return () => {
            if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        };
    }, []);

    // Pause video when switching away from video view
    useEffect(() => {
        if (!showVideo && videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause();
        }
    }, [showVideo]);

    // Use custom event to detect when doctor profiles switch
    useEffect(() => {
        const handleDoctorSwitch = (event: CustomEvent) => {
            const activeDoctorId = event.detail.activeDoctorId;

            // If this is not the active doctor, pause video and switch to images
            if (activeDoctorId !== doctorId) {
                if (videoRef.current && !videoRef.current.paused) {
                    videoRef.current.pause();
                }
                setShowVideo(false);
            }
        };

        // Listen for doctor switch events
        window.addEventListener(DOCTOR_PROFILE_SWITCH_EVENT, handleDoctorSwitch as EventListener);

        return () => {
            window.removeEventListener(
                DOCTOR_PROFILE_SWITCH_EVENT,
                handleDoctorSwitch as EventListener
            );
        };
    }, [doctorId]);

    const handleToggleClick = () => {
        if (showVideo) {
            // Switching from video to images - pause video
            if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        }
        setShowVideo(!showVideo);
    };

    return (
        <Styled.Container>
            {(hasVideo || hasImages) && (
                <Styled.Asset assetHeight={assetHeight}>
                    {hasVideo && hasImages && (
                        <Styled.ToggleButton
                            type="button"
                            onClick={handleToggleClick}
                            isActive={showVideo}
                            aria-pressed={showVideo}
                            title={showVideo ? 'Show Images' : 'Show Video'}
                        >
                            <OxIcon
                                name={showVideo ? 'video-pause-button' : 'video-play-button'}
                                size={20}
                            />
                        </Styled.ToggleButton>
                    )}
                    {showVideo && hasVideo ? (
                        <OxVideoPlayer
                            key={`video-${doctorId}`}
                            ref={videoRef}
                            src={videoForPlayer}
                            controls
                            playsInline
                            autoPlay
                            disableRatioPadding
                        />
                    ) : (
                        hasImages && (
                            <Styled.AssetImage
                                fluidImages={fluidImages}
                                assetHeight={assetHeight}
                            />
                        )
                    )}
                </Styled.Asset>
            )}
            <Styled.ContentWrapper isFirst={isFirst} contentHeight={contentHeight}>
                <Styled.Headers>
                    {label && <OxTextTagline>{label}</OxTextTagline>}
                    {title && <OxTextHeading>{title}</OxTextHeading>}
                </Styled.Headers>
                {title && <OxTextParagraph>{description}</OxTextParagraph>}
            </Styled.ContentWrapper>
        </Styled.Container>
    );
};
